import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AgencyProvider } from "./contexts/AgencyContext";
import { RealtimeProvider } from "./contexts/RealtimeContext";
import { AskAlignedProvider } from "./contexts/AskAlignedContext";
import ProtectedRoute from "./components/ProtectedRoute";
import AppShell from "./components/layout/AppShell";

import Login from "./pages/auth/Login";
import SetPassword from "./pages/auth/SetPassword";
import ForgotPassword from "./pages/auth/ForgotPassword";

import Landing from "./pages/public/Landing";
import About from "./pages/public/About";
import Apply from "./pages/public/Apply";
import Track from "./pages/public/Track";

import Dashboard from "./pages/Dashboard";
import AgencyWelcomeCenter from "./pages/AgencyWelcomeCenter";
import ComingSoon from "./pages/ComingSoon";
import HiringQueue from "./pages/hiring/HiringQueue";
import ApplicantDetail from "./pages/hiring/ApplicantDetail";
import Settings from "./pages/settings/Settings";
import SecurityCenter from "./pages/settings/SecurityCenter";

// Employee Portal (Sprint 2)
import EmployeeLogin from "./pages/employee/EmployeeLogin";
import CaregiverDashboard from "./pages/employee/CaregiverDashboard";
import MyClients from "./pages/employee/MyClients";
import ClientDetail from "./pages/employee/ClientDetail";
import MySchedule from "./pages/employee/MySchedule";
import VisitCareNotes from "./pages/employee/VisitCareNotes";
import VisitDetail from "./pages/employee/VisitDetail";
import ActiveVisit from "./pages/employee/ActiveVisit";
import CaregiverWorkspace from "./pages/employee/CaregiverWorkspace";
import VisitWorkflow from "./pages/employee/VisitWorkflow";

// Assessment → Care Plan Engine
import AssessmentsList from "./pages/assessments/AssessmentsList";
import AssessmentForm from "./pages/assessments/AssessmentForm";
import CarePlanReview from "./pages/assessments/CarePlanReview";

// Client 360° & Scheduling
import ClientsList from "./pages/clients/ClientsList";
import ClientDashboard from "./pages/clients/ClientDashboard";

// Assignment Center
import AssignmentCenter from "./pages/assignments/AssignmentCenter";
import SmartMatchFlow from "./pages/assignments/SmartMatchFlow";
import DirectAssignFlow from "./pages/assignments/DirectAssignFlow";
import CareTeamCenter from "./pages/assignments/CareTeamCenter";

// Scheduler Calendar
import SchedulerCalendar from "./pages/scheduling/SchedulerCalendar";
import UnifiedCalendar from "./pages/scheduling/UnifiedCalendar";
// Phase 1A: Simplified Visit Creation ("Schedule Care" Experience)
import SimplifiedVisitCreation from "./pages/scheduling/SimplifiedVisitCreation";
// Phase 1B: Coverage Management Engine
import CoverageCenter from "./pages/coverage/CoverageCenter";

// Communication Center
import { CommunicationHub, CommunicationDetail } from "./pages/communications";

// Command Center
import { CommandCenter } from "./pages/command-center";

// Supervisor Review Center & Exception Center (V1.0)
import { ReviewCenter } from "./pages/review-center";
import { ExceptionCenter } from "./pages/exception-center";

// Records Center — Enterprise Records Management
import { RecordsCenter } from "./pages/records-center";

// Compliance Center — Operational Health Dashboard
import ComplianceDashboard from "./pages/compliance-center/ComplianceDashboard";

// AI Operations Coordinator — Executive Intelligence Layer
import OperationsCoordinator from "./pages/ops-coordinator/OperationsCoordinator";

// Payroll Dashboard — Operational Control Center
import PayrollDashboard from "./pages/payroll/PayrollDashboard";

// Billing Dashboard — Revenue Operations Center
import BillingDashboard from "./pages/billing/BillingDashboard";

// Data Migration — Pilot Operations
import DataMigrationWizard from "./pages/migration/DataMigrationWizard";
import MigrationCenter from "./pages/migration/MigrationCenter";

// Agency Setup Wizard — Agency Command Center
import AgencySetupWizard from "./pages/setup/AgencySetupWizard";

// Universal Intake Engine — Foundational Platform Service
import UniversalIntakeDemo from "./pages/intake/UniversalIntakeDemo";

// Client Intake Workflow — Full Digital Onboarding
import IntakeDashboard from "./pages/intake/IntakeDashboard";
import ScheduleAssessment from "./pages/intake/ScheduleAssessment";
import IntakeAssessmentForm from "./pages/intake/AssessmentForm";
import CarePlanGenerator from "./pages/intake/CarePlanGenerator";
import ServiceAgreement from "./pages/intake/ServiceAgreement";
import CaregiverMatching from "./pages/intake/CaregiverMatching";

// Employees List — Integrated with Universal Intake
import EmployeesList from "./pages/employees/EmployeesList";

// AI Operations Studio — V1.1 Preview
import AIOperationsStudio from "./pages/operations/AIOperationsStudio";

// Operational Validation & Executive Briefing — Pilot Certification
import { OperationalValidation, ExecutiveBriefing } from "./pages/operations";

// Pilot Journal — Operational Observations
import { PilotJournal } from "./pages/pilot";

// Platform Snapshot — Agency Health Overview
import { PlatformSnapshot } from "./pages/snapshot";

// Multi-Portal Architecture — Principle 16
import MyAligned from "./pages/portals/MyAligned";
import ExecutivePortal from "./pages/portals/ExecutivePortal";
import HRCompliancePortal from "./pages/portals/HRCompliancePortal";
import OperationsPortal from "./pages/portals/OperationsPortal";
import FinancePortal from "./pages/portals/FinancePortal";

// Workspace Architecture — Workflow-First Navigation
import HomeWorkspace from "./pages/workspaces/HomeWorkspace";
import PeopleWorkspace from "./pages/workspaces/PeopleWorkspace";
import OperationsWorkspace from "./pages/workspaces/OperationsWorkspace";

// Pilot Acknowledgement — Required for first-time users
import PilotAcknowledgement from "./pages/PilotAcknowledgement";
import PilotAcknowledgementGate from "./components/PilotAcknowledgementGate";
import BusinessWorkspace from "./pages/workspaces/BusinessWorkspace";
import OrganizationWorkspace from "./pages/workspaces/OrganizationWorkspace";

// CareHub — Multi-modal Caregiver/Client Entry with AI Recommendations
import CareHub from "./pages/data-entry/SmartDataEntryHub";

// Employee Self-Service
import MyProfile from "./pages/employee/MyProfile";
import CaregiverTimesheet from "./pages/employee/CaregiverTimesheet";
import CaregiverCallout from "./pages/employee/CaregiverCallout";

// Timesheet Review — Office Staff Dashboard
import TimesheetReviewDashboard from "./pages/timesheets/TimesheetReviewDashboard";

// Shift Coverage & Policy Management
import ShiftCoverageDashboard from "./pages/organization/ShiftCoverageDashboard";
import PolicyEditor from "./pages/organization/PolicyEditor";

import "./App.css";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AgencyProvider>
          <RealtimeProvider>
          <AskAlignedProvider>
          <Routes>
            {/* Public */}
            <Route path="/" element={<Landing />} />
            <Route path="/about" element={<About />} />
            <Route path="/login" element={<Login />} />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/reset-password" element={<SetPassword />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/apply/:agencyId" element={<Apply />} />
            <Route path="/track" element={<Track />} />

            {/* Employee Portal (Mobile-First Caregiver Experience) */}
            <Route path="/employee" element={<EmployeeLogin />} />
            <Route
              path="/employee/workspace"
              element={
                <ProtectedRoute requiredRole="employee">
                  <CaregiverWorkspace />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/dashboard"
              element={
                <ProtectedRoute requiredRole="employee">
                  <CaregiverDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/visit/:visitId/workflow"
              element={
                <ProtectedRoute requiredRole="employee">
                  <VisitWorkflow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/clients"
              element={
                <ProtectedRoute requiredRole="employee">
                  <MyClients />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/client/:clientId"
              element={
                <ProtectedRoute requiredRole="employee">
                  <ClientDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/schedule"
              element={
                <ProtectedRoute requiredRole="employee">
                  <MySchedule />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/visit/:visitId/care-notes"
              element={
                <ProtectedRoute requiredRole="employee">
                  <VisitCareNotes />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/visit/:visitId"
              element={
                <ProtectedRoute requiredRole="employee">
                  <VisitDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/employee/visit/:visitId/active"
              element={
                <ProtectedRoute requiredRole="employee">
                  <ActiveVisit />
                </ProtectedRoute>
              }
            />

            {/* ═══════════════════════════════════════════════════════════════
                WORKSPACE ARCHITECTURE — Workflow-First Navigation
                Design Principles:
                - 10-Second Rule: User understands context immediately
                - One-Click Rule: Daily tasks are immediately accessible
                - Workspace Principle: Organized around work, not modules
            ═══════════════════════════════════════════════════════════════ */}
            
            {/* Home Workspace — Operational Command Center */}
            <Route
              path="/home"
              element={
                <ProtectedRoute>
                  <AppShell><HomeWorkspace /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/home/*"
              element={
                <ProtectedRoute>
                  <AppShell><HomeWorkspace /></AppShell>
                </ProtectedRoute>
              }
            />
            
            {/* People Workspace — Employees, Caregivers, HR */}
            <Route
              path="/people"
              element={
                <ProtectedRoute>
                  <AppShell><PeopleWorkspace /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/people/employees"
              element={
                <ProtectedRoute>
                  <AppShell><EmployeesList /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/people/caregivers"
              element={
                <ProtectedRoute>
                  <AppShell><EmployeesList /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/people/hiring"
              element={
                <ProtectedRoute>
                  <AppShell><HiringQueue /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/people/hiring/:id"
              element={
                <ProtectedRoute>
                  <AppShell><ApplicantDetail /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/people/hr"
              element={
                <ProtectedRoute>
                  <AppShell><PeopleWorkspace /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/people/caregivers/new"
              element={
                <ProtectedRoute>
                  <AppShell><CareHub /></AppShell>
                </ProtectedRoute>
              }
            />
            
            {/* Operations Workspace — Clients, Scheduling, Visits */}
            <Route
              path="/operations"
              element={
                <ProtectedRoute>
                  <AppShell><OperationsWorkspace /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/clients"
              element={
                <ProtectedRoute>
                  <AppShell><ClientsList /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/clients/:clientId"
              element={
                <ProtectedRoute>
                  <AppShell><ClientDashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/scheduling"
              element={
                <ProtectedRoute>
                  <AppShell><UnifiedCalendar /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/scheduling/new"
              element={
                <ProtectedRoute>
                  <AppShell><SimplifiedVisitCreation /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/coverage"
              element={
                <ProtectedRoute>
                  <AppShell><CoverageCenter /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/exceptions"
              element={
                <ProtectedRoute>
                  <AppShell><ExceptionCenter /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/operations/communications"
              element={
                <ProtectedRoute>
                  <AppShell><CommunicationHub /></AppShell>
                </ProtectedRoute>
              }
            />
            
            {/* Business Workspace — Payroll, Billing, Reports */}
            <Route
              path="/business"
              element={
                <ProtectedRoute>
                  <AppShell><BusinessWorkspace /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/business/payroll"
              element={
                <ProtectedRoute>
                  <AppShell><PayrollDashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/business/billing"
              element={
                <ProtectedRoute>
                  <AppShell><BillingDashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            
            {/* Organization Workspace — Compliance, Settings */}
            <Route
              path="/organization"
              element={
                <ProtectedRoute>
                  <AppShell><OrganizationWorkspace /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organization/compliance"
              element={
                <ProtectedRoute>
                  <AppShell><ComplianceDashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organization/data-entry"
              element={
                <ProtectedRoute>
                  <AppShell><CareHub /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organization/settings"
              element={
                <ProtectedRoute>
                  <AppShell><Settings /></AppShell>
                </ProtectedRoute>
              }
            />
            
            {/* Timesheet Review Dashboard — Office Staff */}
            <Route
              path="/organization/timesheets"
              element={
                <ProtectedRoute>
                  <AppShell><TimesheetReviewDashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/timesheets/review"
              element={
                <ProtectedRoute>
                  <AppShell><TimesheetReviewDashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            
            {/* Client Intake Workflow Routes */}
            <Route
              path="/intake/schedule/:requestId"
              element={
                <ProtectedRoute>
                  <AppShell><ScheduleAssessment /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/intake/assessment/:requestId"
              element={
                <ProtectedRoute>
                  <AppShell><IntakeAssessmentForm /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/intake/care-plan/:requestId"
              element={
                <ProtectedRoute>
                  <AppShell><CarePlanGenerator /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/intake/agreement/:requestId"
              element={
                <ProtectedRoute>
                  <AppShell><ServiceAgreement /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/intake/match/:requestId"
              element={
                <ProtectedRoute>
                  <AppShell><CaregiverMatching /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Staff Authenticated */}
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AppShell><AgencyWelcomeCenter /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard/hiring"
              element={
                <ProtectedRoute>
                  <AppShell><Dashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hiring"
              element={
                <ProtectedRoute>
                  <AppShell><HiringQueue /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/hiring/:id"
              element={
                <ProtectedRoute>
                  <AppShell><ApplicantDetail /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <AppShell><Settings /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/security"
              element={
                <ProtectedRoute>
                  <AppShell><SecurityCenter /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/coming-soon/:module"
              element={
                <ProtectedRoute>
                  <AppShell><ComingSoon /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Assessment → Care Plan Engine */}
            <Route
              path="/assessments"
              element={
                <ProtectedRoute>
                  <AssessmentsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assessments/:assessmentId"
              element={
                <ProtectedRoute>
                  <AssessmentForm />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assessments/:assessmentId/care-plan"
              element={
                <ProtectedRoute>
                  <CarePlanReview />
                </ProtectedRoute>
              }
            />

            {/* Client 360° & Scheduling */}
            <Route
              path="/clients"
              element={
                <ProtectedRoute>
                  <ClientsList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clients/:clientId"
              element={
                <ProtectedRoute>
                  <ClientDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/scheduling"
              element={
                <ProtectedRoute>
                  <SchedulerCalendar />
                </ProtectedRoute>
              }
            />
            {/* Phase 1A: Simplified Visit Creation */}
            <Route
              path="/schedule-care"
              element={
                <ProtectedRoute>
                  <SimplifiedVisitCreation />
                </ProtectedRoute>
              }
            />
            {/* Phase 1B: Coverage Management Engine */}
            <Route
              path="/coverage"
              element={
                <ProtectedRoute>
                  <CoverageCenter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/coverage/:eventId"
              element={
                <ProtectedRoute>
                  <CoverageCenter />
                </ProtectedRoute>
              }
            />

            {/* Care Team Center — Client-Caregiver Relationships */}
            <Route
              path="/care-team"
              element={
                <ProtectedRoute>
                  <AppShell><CareTeamCenter /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Data Migration — Pilot Operations */}
            <Route
              path="/migration"
              element={
                <ProtectedRoute>
                  <AppShell><MigrationCenter /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/data-migration"
              element={
                <ProtectedRoute>
                  <AppShell><DataMigrationWizard /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Agency Setup Wizard — Command Center */}
            <Route
              path="/agency-setup"
              element={
                <ProtectedRoute>
                  <AppShell><AgencySetupWizard /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Universal Intake Engine — Demo */}
            <Route
              path="/intake-demo"
              element={
                <ProtectedRoute>
                  <AppShell><UniversalIntakeDemo /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Assignment Center */}
            <Route
              path="/assignment-center"
              element={
                <ProtectedRoute>
                  <AssignmentCenter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assignment-center/smart-match"
              element={
                <ProtectedRoute>
                  <SmartMatchFlow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/assignment-center/direct-assign"
              element={
                <ProtectedRoute>
                  <DirectAssignFlow />
                </ProtectedRoute>
              }
            />

            {/* Communication Center */}
            <Route
              path="/communications"
              element={
                <ProtectedRoute>
                  <AppShell><CommunicationHub /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/communications/:id"
              element={
                <ProtectedRoute>
                  <AppShell><CommunicationDetail /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Command Center */}
            <Route
              path="/command-center"
              element={
                <ProtectedRoute>
                  <AppShell><CommandCenter /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Supervisor Review Center */}
            <Route
              path="/review"
              element={
                <ProtectedRoute>
                  <AppShell><ReviewCenter /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Exception Center */}
            <Route
              path="/exceptions"
              element={
                <ProtectedRoute>
                  <AppShell><ExceptionCenter /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Operational Validation Dashboard */}
            <Route
              path="/validation"
              element={
                <ProtectedRoute>
                  <AppShell><OperationalValidation /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Executive Morning Briefing */}
            <Route
              path="/briefing"
              element={
                <ProtectedRoute>
                  <AppShell><ExecutiveBriefing /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Pilot Journal */}
            <Route
              path="/pilot-journal"
              element={
                <ProtectedRoute>
                  <AppShell><PilotJournal /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Platform Snapshot */}
            <Route
              path="/snapshot"
              element={
                <ProtectedRoute>
                  <AppShell><PlatformSnapshot /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Records Center */}
            <Route
              path="/records"
              element={
                <ProtectedRoute>
                  <AppShell><RecordsCenter /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Employees List */}
            <Route
              path="/employees"
              element={
                <ProtectedRoute>
                  <AppShell><EmployeesList /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* AI Operations Studio — V1.1 Preview */}
            <Route
              path="/operations-studio"
              element={
                <ProtectedRoute>
                  <AppShell><AIOperationsStudio /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* ============ MULTI-PORTAL ARCHITECTURE ============ */}
            
            {/* My Leynix — Caregiver/Employee Portal (Mobile-First) */}
            <Route
              path="/my-leynix/*"
              element={
                <ProtectedRoute>
                  <MyAligned />
                </ProtectedRoute>
              }
            />
            
            {/* Caregiver Self-Service Profile */}
            <Route
              path="/my-leynix/profile"
              element={
                <ProtectedRoute>
                  <MyProfile />
                </ProtectedRoute>
              }
            />
            
            {/* Caregiver Timesheet & Care Notes */}
            <Route
              path="/my-leynix/timesheet"
              element={
                <ProtectedRoute>
                  <CaregiverTimesheet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-leynix/timesheet/:timesheetId"
              element={
                <ProtectedRoute>
                  <CaregiverTimesheet />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-leynix/callout"
              element={
                <ProtectedRoute>
                  <CaregiverCallout />
                </ProtectedRoute>
              }
            />
            
            {/* Shift Coverage & Policy Management */}
            <Route
              path="/organization/coverage"
              element={
                <ProtectedRoute>
                  <AppShell><ShiftCoverageDashboard /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organization/policies"
              element={
                <ProtectedRoute>
                  <AppShell><PolicyEditor /></AppShell>
                </ProtectedRoute>
              }
            />
            <Route
              path="/organization/settings/policies"
              element={
                <ProtectedRoute>
                  <AppShell><PolicyEditor /></AppShell>
                </ProtectedRoute>
              }
            />
            
            {/* Legacy route redirect */}
            <Route
              path="/my-aligned/*"
              element={
                <ProtectedRoute>
                  <MyAligned />
                </ProtectedRoute>
              }
            />
            
            {/* Executive Portal */}
            <Route
              path="/portal/executive"
              element={
                <ProtectedRoute>
                  <AppShell><ExecutivePortal /></AppShell>
                </ProtectedRoute>
              }
            />
            
            {/* HR & Compliance Portal */}
            <Route
              path="/portal/hr"
              element={
                <ProtectedRoute>
                  <AppShell><HRCompliancePortal /></AppShell>
                </ProtectedRoute>
              }
            />
            
            {/* Operations Portal */}
            <Route
              path="/portal/operations"
              element={
                <ProtectedRoute>
                  <AppShell><OperationsPortal /></AppShell>
                </ProtectedRoute>
              }
            />
            
            {/* Finance Portal */}
            <Route
              path="/portal/finance"
              element={
                <ProtectedRoute>
                  <AppShell><FinancePortal /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Compliance Center */}
            <Route
              path="/compliance"
              element={
                <ProtectedRoute>
                  <AppShell><ComplianceDashboard /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* AI Operations Coordinator */}
            <Route
              path="/ops"
              element={
                <ProtectedRoute>
                  <AppShell><OperationsCoordinator /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Payroll Dashboard */}
            <Route
              path="/payroll"
              element={
                <ProtectedRoute>
                  <AppShell><PayrollDashboard /></AppShell>
                </ProtectedRoute>
              }
            />

            {/* Billing Dashboard — Revenue Operations */}
            <Route
              path="/billing"
              element={
                <ProtectedRoute>
                  <AppShell><BillingDashboard /></AppShell>
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </AskAlignedProvider>
          </RealtimeProvider>
        </AgencyProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
